// ============================================================================
// BESPOKIAN Pharmacist Cockpit - API Client
// Centralized HTTP client with authentication and error handling
// ============================================================================

interface ApiError {
  message: string;
  code: string;
  status: number;
}

type SuccessEnvelope<T> = {
  success: true;
  data: T;
};

type ErrorEnvelope = {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

type ApiEnvelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = '/api/v1') {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('accessToken');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // refresh token cookie flow
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as Partial<ErrorEnvelope> &
        Partial<ApiError>;

      const error: ApiError = {
        message:
          (errorData as ErrorEnvelope)?.error?.message ??
          (errorData as ApiError)?.message ??
          'An error occurred',
        code:
          (errorData as ErrorEnvelope)?.error?.code ??
          (errorData as ApiError)?.code ??
          'UNKNOWN_ERROR',
        status: response.status,
      };

      // Handle 401 - unauthorized
      if (response.status === 401) {
        this.setToken(null);
        const isLoginRequest = endpoint === '/auth/login';
        const isAlreadyOnLoginPage = window.location.pathname === '/login';

        // Avoid hard-redirect loops on the login request itself; let the UI show the error.
        if (!isLoginRequest && !isAlreadyOnLoginPage) {
          window.location.href = '/login';
        }
      }

      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    const body = (await response.json()) as ApiEnvelope<T>;

    if ('success' in body && body.success === false) {
      const error: ApiError = {
        code: body.error.code,
        message: body.error.message,
        status: 400,
      };
      throw error;
    }

    // Default to "envelope" shape used by the consolidated backend
    if ('success' in body && body.success === true) {
      return body.data;
    }

    // Fallback for legacy endpoints returning raw data
    return body as unknown as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();
export const apiClient = api;

// API Response wrapper type
export interface ApiResponse<T> {
  data: T;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
